import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Link as MuiLink,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from "../Header";
import Footer from "../Footer";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import config from "../common/Config";
import * as XLSX from "xlsx";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const MisReport = () => {
  const [reportrange, setReportrange] = useState("");
  const [dates, setDates] = useState([null, null]);
  const [reportDate, setReportDate] = useState("");
  const [salesRapOptions, setSalesRapOptions] = useState([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const [salesRepInputValue, setSalesRepInputValue] = useState('');
  const [filteredSalesRepOptions, setFilteredSalesRepOptions] = useState([]);
  const [salesRap, setSalesRap] = useState("");
  const [routeOptions, setRouteOptions] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeInputValue, setRouteInputValue] = useState('');
  const [filteredRouteOptions, setFilteredRouteOptions] = useState([]);
  const [salesRoute, setSalesRoute] = useState("");
  const [data, setData] = useState([]);
  const [footerTotals, setFooterTotals] = useState({
    orderTotal: '0.00',
    acceptTotal: '0.00',
    pickingTotal: '0.00',
    deliveredTotal: '0.00',
    invoiceTotal: '0.00',
    vatTotal: '0.00'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem('token');
  const company_tax_type = localStorage.getItem("company_tax_type") || "VAT";

  const handleDateChange = (value) => {
    setDates(value);
    const startDate = value[0] ? format(value[0], "dd/MM/yyyy") : "";
    const endDate = value[1] ? format(value[1], "dd/MM/yyyy") : "";
    setReportrange(startDate ? startDate + " - " + endDate : "");
    setReportDate(startDate + " To Date : " + endDate);
    setPage(0);
  };

  const fetchSalesRapOptions = async () => {
    const apiSalesRapUrl = `${config.API_URL}/getSalesRep`;
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const body = new FormData();
      if (useEncrypt) {
        const ep = encryptPayload({});
        if (ep) {
          body.append('encrypted_payload', ep);
        }
      }
      const response = await fetch(apiSalesRapUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: body.entries().next().done ? undefined : body,
      });

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const formattedOptions = data.data.map((item) => ({
        label: `${item.first_name} ${item.last_name}`,
        value: item.id,
      }));
      setSalesRapOptions(formattedOptions);
      setFilteredSalesRepOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchSalesRoute = async () => {
    const apiSalesRouteUrl = `${config.API_URL}/getRoute`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();
      const formattedOptions = data.data.map((item) => ({
        label: `${item.route_number}`,
        value: item.id,
      }));
      setRouteOptions(formattedOptions);
      setFilteredRouteOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: String(page + 1),
        start: String(page * rowsPerPage),
        length: String(rowsPerPage),
        searchValue,
        reportrange,
        sales_rep: salesRap,
        route_id: salesRoute,
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/mis_report`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
        setReportDate(json.startdate + " To Date : " + json.enddate);

        setFooterTotals({
          orderTotal: parseFloat(json.order_amt?.replace(/,/g, "") || 0).toFixed(2),
          acceptTotal: parseFloat(json.accept_amt?.replace(/,/g, "") || 0).toFixed(2),
          pickingTotal: parseFloat(json.picking_amt?.replace(/,/g, "") || 0).toFixed(2),
          deliveredTotal: parseFloat(json.deliver_amt?.replace(/,/g, "") || 0).toFixed(2),
          invoiceTotal: parseFloat(json.deliver_amt?.replace(/,/g, "") || 0).toFixed(2),
          vatTotal: parseFloat(json.total_tax?.replace(/,/g, "") || 0).toFixed(2)
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, searchValue, salesRap, salesRoute]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSalesRapOptions();
    fetchSalesRoute();
  }, [accessToken]);

  useEffect(() => {
    if (reportrange) {
      const timer = setTimeout(() => {
        fetchReport();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fetchReport]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setPage(0);
      fetchReport();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSalesRepInputChange = (event, newInputValue) => {
    setSalesRepInputValue(newInputValue);
    const filtered = salesRapOptions.filter(option =>
      option.label.toLowerCase().includes(newInputValue.toLowerCase())
    );
    setFilteredSalesRepOptions(filtered);
  };

  const handleSalesRepChange = (event, newValue) => {
    setSelectedSalesRep(newValue);
    setSalesRap(newValue ? newValue.value : '');
  };

  const handleRouteInputChange = (event, newInputValue) => {
    setRouteInputValue(newInputValue);
    const filtered = routeOptions.filter(option =>
      option.label.toLowerCase().includes(newInputValue.toLowerCase())
    );
    setFilteredRouteOptions(filtered);
  };

  const handleRouteChange = (event, newValue) => {
    setSelectedRoute(newValue);
    setSalesRoute(newValue ? newValue.value : '');
  };

  const handleSubmit = () => {
    setPage(0);
    fetchReport();
  };

  const exportToExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    const plain = {
      draw: "1",
      start: "0",
      length: totalCount,
      searchValue,
      reportrange,
      sales_rep: salesRap,
      route_id: salesRoute,
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        formData.append("encrypted_payload", ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ""));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ""));
    }

    const apiActivePdtUrl = `${config.API_URL}/mis_report`;

    try {
      const response = await fetch(apiActivePdtUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch Products");
      }

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const exportData = data.data.map((product) => ({
        Date: product.date || "",
        "Customer (A/C)": product.company
          ? `${product.company} (${product.accound_no || ""})`
          : product.accound_no || "",
        "Order Number": product.reference_no || "",
        Order: product.order_amt
          ? parseFloat(product.order_amt.toString().replace(/,/g, "")).toFixed(2)
          : "0.00",
        Accept: product.accept_amt
          ? parseFloat(product.accept_amt.toString().replace(/,/g, "")).toFixed(2)
          : "0.00",
        Picking: product.picking_amt
          ? parseFloat(product.picking_amt.toString().replace(/,/g, "")).toFixed(2)
          : "0.00",
        Delivered: product.deliver_amt
          ? parseFloat(product.deliver_amt.toString().replace(/,/g, "")).toFixed(2)
          : "0.00",
        Invoice: product.invoice_amt
          ? parseFloat(product.invoice_amt.toString().replace(/,/g, "")).toFixed(2)
          : "0.00",
        [company_tax_type]: product.total_tax
          ? parseFloat(product.total_tax.toString().replace(/,/g, "")).toFixed(2)
          : "0.00",
        Status: product.sale_status || "",
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);

      Object.keys(ws).forEach((cell) => {
        if (cell[0] !== "!") {
          const cellValue = ws[cell].v;
          if (!isNaN(cellValue)) {
            ws[cell].t = "n";
          }
        }
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Mis Report");

      const now = new Date();
      const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const formattedTime = `${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
      const filename = `mis-report-${formattedDate}-${formattedTime}.xlsx`;

      XLSX.writeFile(wb, filename);
    } catch (error) {
    }
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
          <Paper className="paper-style"
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography className="page-title" mb={1}>
                MIS Report
              </Typography>

              <Grid2 container spacing={1}>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    className="input-border"
                    fullWidth
                    size="small"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-fill" sx={{ color: '#0e0f0fef' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    showOneCalendar
                    onChange={handleDateChange}
                    format="dd/MM/yyyy"
                    character=" - "
                    style={{ width: '100%' }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                  <Autocomplete
                    className="input-border text-field"
                    size="small"
                    options={filteredSalesRepOptions}
                    value={selectedSalesRep}
                    onChange={handleSalesRepChange}
                    inputValue={salesRepInputValue}
                    onInputChange={handleSalesRepInputChange}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField {...params} label="Sales Rep" placeholder="Select Sales Rep" />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                  <Autocomplete
                    className="input-border text-field"
                    size="small"
                    options={filteredRouteOptions}
                    value={selectedRoute}
                    onChange={handleRouteChange}
                    inputValue={routeInputValue}
                    onInputChange={handleRouteInputChange}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField {...params} label="Route" placeholder="Select Route" />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                  <Stack display="flex" spacing={1} flexDirection={{ xs: "column", sm: "row" }}>
                    <Button
                      className="button-variant-contained"
                      variant="contained"
                      fullWidth
                      onClick={handleSubmit}
       
                    >
                      Submit
                    </Button>
                    <Button
                      variant="outlined"
                      className="button-variant-outlined"
                      onClick={exportToExcel}
                      startIcon={<Iconify icon="eva:download-outline" width={18} />}
                      fullWidth
                    >
                      Export
                    </Button>
                  </Stack>
                </Grid2>
              </Grid2>

              {reportDate && (
                <Box sx={{mt:1, p: 2, backgroundColor: '#f5f7fa', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#212b36' }}>
                    MIS Report From Date : {reportDate}
                  </Typography>
                </Box>
              )}
            </Box>

            <TableContainer
            >
              <Table size="medium" 
              className="table-cell-border"
              sx={{ tableLayout: "fixed", width: '100%' }}>
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell  width="70px">Sr. No</TableCell>
                    <TableCell width="100px">Date</TableCell>
                    <TableCell width="250px">Customer (A/C)</TableCell>
                    <TableCell width="150px">Order No</TableCell>
                    <TableCell align="right" width="100px">Order</TableCell>
                    <TableCell align="right" width="100px">Accept</TableCell>
                    <TableCell align="right" width="100px">Picking</TableCell>
                    <TableCell align="right" width="100px">Delivered</TableCell>
                    <TableCell align="right" width="100px">Invoice</TableCell>
                    <TableCell align="right" width="100px">{company_tax_type}</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((row, index) => (
                      <TableRow 
                      key={index} 
                      hover 
                      className="table-cell-style"
                      >
                        <TableCell>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>{row.date || ''}</TableCell>
                        <TableCell>
                          {row.company ? `${row.company} (${row.accound_no || ''})` : row.accound_no || ''}
                        </TableCell>
                        <TableCell>
                          <MuiLink
                            href={`${config.BASE_URL}/reports/mis_report/print/${row.id}`}
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {row.reference_no || ''}
                          </MuiLink>
                        </TableCell>
                        <TableCell align="right">{row.order_amt || 0}</TableCell>
                        <TableCell align="right">{row.accept_amt || 0}</TableCell>
                        <TableCell align="right">{row.picking_amt || 0}</TableCell>
                        <TableCell align="right">{row.deliver_amt || 0}</TableCell>
                        <TableCell align="right">{row.invoice_amt || 0}</TableCell>
                        <TableCell align="right">{row.total_tax || 0}</TableCell>
                        <TableCell align="center">{row.sale_status || ''}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4, color: '#0e0f0fef', fontSize: '0.75rem' }}>
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ backgroundColor: '#f5f7fa',
                    "& .MuiTableCell-root": {
                      fontSize: '0.8rem',
                      color: '#212b36',
                      fontWeight: 600,
                      textAlign: 'center',
                      py: 1.2,
                  
                    }
                   }}>
                    <TableCell colSpan={4} align="right">
                      Total:
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.orderTotal}
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.acceptTotal}
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.pickingTotal}
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.deliveredTotal}
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.invoiceTotal}
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.vatTotal}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              className="pagination-styles"
            />
          </Paper>
        </Box>
   
    </>
  );
};

export default MisReport;
